/**
 * @fileoverview Data transformation utilities for import/export operations.
 * Provides regex-based pattern matching and replacement functionality
 * for transforming bot configuration data during import/export.
 * @module helperMethods/import_export/transformations
 */

import _ from "lodash";

/**
 * Configuration for a single transformation rule.
 */
interface Transformation {
  enabled: boolean;
  displayName: string;
  type: string;
  path: string;
  expectedPattern: string;
  replacement: string;
}

/**
 * Escapes special whitespace characters in a regex string.
 * Converts newlines, tabs, and other special characters to their escaped forms.
 *
 * @param {string} regex - Regex string to escape
 * @returns {string} Escaped regex string
 *
 * @example
 * escapeSpecialCharacters('hello\nworld\t');
 * // Returns: 'hello\\nworld\\t'
 */
export function escapeSpecialCharacters(regex: string): string {
  return regex
    .replace(/\n/g, "\\n")
    .replace(/\t/g, "\\t")
    .replace(/\v/g, "\\v")
    .replace(/\0/g, "\\0")
    .replace(/\f/g, "\\f")
    .replace(/\r/g, "\\r");
}

/**
 * Converts a regex string (with or without delimiters) to a RegExp object.
 * Supports various regex string formats including /pattern/flags, \/pattern\/flags, or plain patterns.
 *
 * @param {string} regex - Regex string to convert
 * @returns {RegExp | undefined} RegExp object, or undefined if parsing fails
 *
 * @example
 * convertRegexStringToRegex('/^test$/gi');
 * // Returns: RegExp object with pattern '^test$' and flags 'gi'
 *
 * @example
 * convertRegexStringToRegex('world');
 * // Returns: RegExp object with pattern 'world' and no flags
 */
export function convertRegexStringToRegex(regex: string) {
  const escapedPattern = escapeSpecialCharacters(regex);
  const regexMatches = escapedPattern.match(/(?:\\\/(.*)\\\/|\/(.*)\/|(.*))([gimsuy]*)/);
  if (regexMatches !== null) {
    const [wholeStringMatch, firstTypeMatch, secondTypeMatch, thirdTypeMatch, flags] = regexMatches;
    const regexContent = firstTypeMatch || secondTypeMatch || thirdTypeMatch;
    return new RegExp(regexContent, flags);
  }
  return;
}

/**
 * Applies multiple transformation rules to a data object.
 * Each transformation uses regex pattern matching to find and replace values.
 *
 * @param {Object} value - Data object to transform
 * @param {Transformation[]} transformations - Array of transformation rules to apply
 * @returns {Object} Transformed data object
 *
 * @example
 * const transformed = transformData(
 *   { content: { text: 'Hello World' } },
 *   [{ path: 'content.text', expectedPattern: '/World/', replacement: 'Universe', enabled: true }]
 * );
 * // Returns: { content: { text: 'Hello Universe' } }
 */
export function transformData(value: { [key: string]: any }, transformations: Transformation[]) {
  return _.reduce(
    transformations,
    (accum, transformation) => {
      const expectedPattern = convertRegexStringToRegex(transformation.expectedPattern);
      const replacement = transformation.replacement;
      const paths = transformation.path.split(".");
      if (expectedPattern) {
        traversePaths(accum, paths, 0, expectedPattern, replacement);
      }
      return accum;
    },
    value
  );
}

/**
 * Recursively traverses object paths to apply regex transformations at target locations.
 * Handles both single values and arrays of values at the target path.
 *
 * @param {Object} value - Root object to traverse
 * @param {string[]} paths - Array of path segments to traverse
 * @param {number} index - Current position in the paths array
 * @param {RegExp} expectedPattern - Regex pattern to match
 * @param {string} replacement - Replacement string
 * @returns {void} Modifies the value object in place
 *
 * @example
 * const data = { messages: { text: ['Hello World', 'Goodbye World'] } };
 * traversePaths(data, ['messages', 'text'], 0, /World/g, 'Universe');
 * // data.messages.text is now ['Hello Universe', 'Goodbye Universe']
 */
export function traversePaths(
  value: { [key: string]: any },
  paths: string[],
  index: number,
  expectedPattern: RegExp,
  replacement: string
) {
  const data = _.get(value, paths[index]);
  if (!data) return;

  const endOfPathToTraverse = index === paths.length - 1;
  const isToBeIterated = Array.isArray(data);
  if (endOfPathToTraverse) {
    if (isToBeIterated) {
      const replacedData = _.map(data, (text) => {
        return text.replace(expectedPattern, replacement);
      });

      _.set(value, paths[index], replacedData);
    } else {
      const replacedData = data.replace(expectedPattern, replacement);

      _.set(value, paths[index], replacedData);
    }
  } else {
    let dataToProcess = data;
    if (!isToBeIterated) {
      dataToProcess = [data];
    }
    _.forEach(dataToProcess, (data) => {
      traversePaths(data, paths, index + 1, expectedPattern, replacement);
    });
  }
}

/**
 * Transforms bot configuration data using type-specific transformation rules.
 * Applies transformations to nodes (content, trigger, etc.) based on their identifier type.
 *
 * @param {Object} nodesToSave - Dictionary of nodes to transform, keyed by identifier
 * @param {Transformation[]} transformations - Array of transformation rules with type filters
 * @returns {Object} Transformed bot data with same structure
 *
 * @example
 * const transformed = transformBotData(
 *   {
 *     'content_node1': { text: 'Old Value' },
 *     'trigger_t1': { pattern: 'Old Pattern' }
 *   },
 *   [
 *     { type: 'content', path: 'text', expectedPattern: '/Old/', replacement: 'New', enabled: true }
 *   ]
 * );
 * // Only content nodes are transformed, triggers remain unchanged
 */
export function transformBotData(
  nodesToSave: { [key: string]: any },
  transformations: Transformation[]
) {
  // Transform all content, trigger, etc
  const transformedBotData = _.reduce(
    nodesToSave,
    (accum, data, identifier) => {
      const filteredTransformations = _.filter(
        transformations,
        (transformation) => transformation.enabled && identifier.startsWith(transformation.type)
      );

      const noTransformationsToApply = filteredTransformations.length === 0;
      if (noTransformationsToApply) {
        accum[identifier] = data;
      } else {
        // Transform each individual content and trigger entry
        const clonedData = _.cloneDeep(data);
        const transformedData = _.reduce(
          clonedData,
          (accum, value, id) => {
            accum[id] = transformData(value, filteredTransformations);
            return accum;
          },
          {} as { [identifier: string]: any }
        );
        accum[identifier] = transformedData;
      }
      return accum;
    },
    {} as { [identifier: string]: any }
  );
  return transformedBotData;
}
